import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const PathwayPagination = ({ currentPage, totalPages, onPageChange, loading = false, show = true }) => {
    if (!show || loading || !totalPages || totalPages <= 1) return null;

    const pages = buildPageRange(currentPage, totalPages);

    return (
        <Nav role="navigation" aria-label="Seitennavigation">
            <PageButton
                type="button"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 0}
                aria-label="Vorherige Seite"
            >
                <IoIosArrowBack />
            </PageButton>

            {pages.map((page, index) =>
                page === "..." ? (
                    <Ellipsis key={index} aria-hidden="true">
                        …
                    </Ellipsis>
                ) : (
                    <PageButton
                        key={index}
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        $active={page - 1 === currentPage}
                        aria-label={`Seite ${page}`}
                        aria-current={page - 1 === currentPage ? "page" : undefined}
                    >
                        {page}
                    </PageButton>
                )
            )}

            <PageButton
                type="button"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                aria-label="Nächste Seite"
            >
                <IoIosArrowForward />
            </PageButton>
        </Nav>
    );
};

PathwayPagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    show: PropTypes.bool,
};

export default PathwayPagination;

function buildPageRange(currentPage, totalPages) {
    const page = currentPage + 1;

    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const left = Math.max(page - 1, 1);
    const right = Math.min(page + 1, totalPages);
    const showLeftDots = left > 2;
    const showRightDots = right < totalPages - 1;

    if (!showLeftDots && showRightDots) {
        return [...Array.from({ length: 5 }, (_, i) => i + 1), "...", totalPages];
    }
    if (showLeftDots && !showRightDots) {
        return [1, "...", ...Array.from({ length: 5 }, (_, i) => totalPages - 4 + i)];
    }
    if (showLeftDots && showRightDots) {
        return [1, "...", left, page, right, "...", totalPages];
    }
    return Array.from({ length: totalPages }, (_, i) => i + 1);
}

const Nav = styled.nav`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
`;

const PageButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid ${(p) => (p.$active ? "var(--pw-primary)" : "var(--border-hairline)")};
    border-radius: var(--pw-radius-md);
    background: ${(p) => (p.$active ? "var(--pw-primary)" : "transparent")};
    color: ${(p) => (p.$active ? "var(--text-on-brand)" : "var(--text-body)")};
    font-size: var(--pw-text-sm);
    font-weight: ${(p) => (p.$active ? "var(--pw-weight-semibold)" : "var(--pw-weight-regular)")};
    cursor: pointer;
    transition: background 120ms, border-color 120ms, color 120ms;
    line-height: 1;

    &:hover:not(:disabled):not([aria-current]) {
        background: var(--pw-neutral-100);
        border-color: var(--border-regular);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;

const Ellipsis = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    color: var(--text-muted);
    font-size: var(--pw-text-sm);
    user-select: none;
`;
